import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'constants/queryKeys';
import { accountManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { TAdminAccount } from 'types';
import { toErrorMessage } from './utils';

export type UseUpdateAdminAccountOptions = {
  onSuccess?: () => void;
};

export type UseUpdateAdminAccountResult<TRequest> = {
  updateAsync: (accountId: number, request: TRequest) => Promise<void>;
  isSubmitting: boolean;
  error: string | undefined;
};

type UpdateParams<TRequest> = {
  accountId: number;
  request: TRequest;
};

export function useUpdateAdminAccountBase<TRequest>(
  apiFn: (accountId: number, request: TRequest) => Promise<TAdminAccount>,
  options: UseUpdateAdminAccountOptions = {},
): UseUpdateAdminAccountResult<TRequest> {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = accountManagementStrings.edit;

  const mutation = useMutation({
    mutationFn: async ({ accountId, request }: UpdateParams<TRequest>) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return apiFn(accountId, request);
    },
    onSuccess: async (_response, variables) => {
      if (!user) return;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.ADMIN_ACCOUNTS, user.uid],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.ADMIN_ACCOUNT_DETAIL,
            variables.accountId,
            user.uid,
          ],
        }),
      ]);

      options.onSuccess?.();
    },
  });

  const updateAsync = async (accountId: number, request: TRequest) => {
    await mutation.mutateAsync({ accountId, request });
  };

  const error = mutation.error
    ? toErrorMessage(mutation.error, strings.errors.updateFailed)
    : undefined;

  return {
    updateAsync,
    isSubmitting: mutation.isPending,
    error,
  };
}
